import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { message, Modal } from "antd";
import { FileNotesDocument as FileNotesDocumentIcon } from "assets/icons";
import YmsDrawer from "components/drawer";
import RenderTable from "components/table";
import { bindActionCreators } from "redux";
import { Creators as ProcessActions } from "store/process/process-duck";
import { IDocumentList, IEditProcess, IItems } from "store/process/process-model";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import useTranslation from "utils/hooks/useTranslation";
import EditForm from "./../components/editForm";
import ColumnsTable from "./columnsTable";
import * as S from "./styled/index.styled";

interface IPropsScreenProcessGrid {
  items?: Array<IItems>;
  totalItems?: number;
  orderBy?: string;
  ascending?: string;
  itemsByPage?: number;
  pageIndex?: number;
  isAdmin?: boolean;
  searchFilter?: string;
  documentsList: Array<IDocumentList>;
  editProcess: IEditProcess;
  visibleDrawerEditProcess: boolean;
  form: any;
  eventInboundActive: boolean;
  eventOutboundActive: boolean;
  formEditModified: boolean;
  visibleDocuments: boolean;
  isLoadingTable: boolean;
  allowImageCapture: boolean;
}
interface IDispatchProps {
  processChangeFieldValue: (...args) => void;
  processHideDrawerEditProcess: (...args) => void;
  processShowDrawerEditProcess: (...args) => void;
  processFetchRegisterAsync: (...args) => void;
  processDeleteByIdAsync: (...args) => void;
  processFetchDocumentsAsync: (...args) => void;
  handleOnConfirmDrawerUpdateProcess: (...args) => void;
  processUpdateFormModified: (...args) => void;
  processEventInboundActive: (...args) => void;
  processEventOutboundActive: (...args) => void;
  processUpdateVisibleDocuments: (...args) => void;
  processFetchByIdAsync: (...args) => void;
  processImageCapturesActive: (...args) => void;
}

type IProps = IPropsScreenProcessGrid & IDispatchProps;

const ProcessGrid = ({
  form,
  items,
  totalItems,
  isAdmin,
  pageIndex,
  searchFilter,
  documentsList,
  editProcess,
  visibleDrawerEditProcess,
  formEditModified,
  eventInboundActive,
  eventOutboundActive,
  isLoadingTable,
  allowImageCapture,
  processHideDrawerEditProcess,
  processDeleteByIdAsync,
  processFetchRegisterAsync,
  processChangeFieldValue,
  processFetchDocumentsAsync,
  processShowDrawerEditProcess,
  handleOnConfirmDrawerUpdateProcess,
  processUpdateFormModified,
  processEventInboundActive,
  processEventOutboundActive,
  processFetchByIdAsync,
  processUpdateVisibleDocuments,
  processImageCapturesActive,
  visibleDocuments,
}: IProps) => {
  const [visibleModalDelete, setVisibleModalDelete] = useState(false);
  const [visibleModalEditProcess, setVisibleModalEditProcess] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState({ description: null, id: null });
  const PROCESS = useTranslation({ id: "you_are_deleting_the_process" });

  const fetchProcess = useCallback(() => processFetchRegisterAsync(), [processFetchRegisterAsync]);

  useEffect(() => {
    fetchProcess();
  }, [fetchProcess]);

  useEffect(() => {
    if (searchFilter !== "" || searchFilter === null) {
      processChangeFieldValue("pageIndex", 1);
      fetchProcess();
    }
  }, [searchFilter]);

  const handleTableChange = (pagination, sorter) => {
    processChangeFieldValue("orderBy", sorter.field);
    processChangeFieldValue("ascend", sorter.order);
    processChangeFieldValue("itemsByPage", pagination.pageSize);
    processChangeFieldValue("pageIndex", pagination.current);
    fetchProcess();
  };

  const handleClick = ({ id, description }) => {
    setSelectedProcess({ description: description, id: id });
    setVisibleModalDelete(true);
  };

  const handleClickEdit = (data: IEditProcess) => {
    processFetchByIdAsync(data.id);
    processShowDrawerEditProcess();
  };

  const handleClickConfirmDelete = () => {
    processDeleteByIdAsync(selectedProcess);
    setVisibleModalDelete(false);
  };

  const handleClickCancelEditProcess = () => {
    if (formEditModified) {
      message.destroy("messageProcess");
      Modal.destroyAll();
      setVisibleModalEditProcess(true);
    } else {
      setVisibleModalEditProcess(false);
      processHideDrawerEditProcess();
    }
  };

  const handleConfirmModalEditProcess = () => {
    setVisibleModalEditProcess(false);
    processHideDrawerEditProcess();
  };

  return (
    <S.ProcessContainer>
      <RenderTable
        columnsTable={ColumnsTable({ handleClick, handleClickEdit, isAdmin })}
        items={items}
        totalItems={totalItems}
        handleTableChange={handleTableChange}
        pageIndex={pageIndex}
        isLoadingTable={isLoadingTable}
      />
      <Modal
        title={useTranslation({ id: "confirmation_message_delete_process" })}
        maskClosable={false}
        centered
        open={visibleModalDelete}
        onCancel={() => setVisibleModalDelete(false)}
        onOk={() => handleClickConfirmDelete()}
        okText={useTranslation({ id: "confirm" })}
        cancelText={useTranslation({ id: "cancel" })}
        destroyOnClose={true}
      >
        <p>{`${PROCESS}: ${selectedProcess.description}`}</p>
      </Modal>
      <YmsDrawer
        width="900"
        confirmbuttontxt={useTranslation({ id: "save" })}
        cancelbuttontxt={useTranslation({ id: "cancel" })}
        maskClosable={true}
        disabled={false}
        onConfirm={handleOnConfirmDrawerUpdateProcess}
        title={useTranslation({ id: "edit_process" })}
        placement="right"
        icon={<FileNotesDocumentIcon style={S.ProcessIcon} />}
        closable={false}
        onClose={handleClickCancelEditProcess}
        open={visibleDrawerEditProcess}
        destroyOnClose={true}
      >
        <EditForm
          form={form}
          documentsList={documentsList}
          editProcess={editProcess}
          processFetchDocumentsAsync={processFetchDocumentsAsync}
          visibleDrawerEditProcess={visibleDrawerEditProcess}
          processUpdateFormModified={processUpdateFormModified}
          eventInboundActive={eventInboundActive}
          eventOutboundActive={eventOutboundActive}
          processEventInboundActive={processEventInboundActive}
          processEventOutboundActive={processEventOutboundActive}
          processUpdateVisibleDocuments={processUpdateVisibleDocuments}
          visibleDocuments={visibleDocuments}
          allowImageCapture={allowImageCapture}
          processImageCapturesActive={processImageCapturesActive}
        />
      </YmsDrawer>
      <Modal
        title={useTranslation({ id: "edit_process" })}
        maskClosable={false}
        centered
        open={visibleModalEditProcess}
        onCancel={() => setVisibleModalEditProcess(false)}
        onOk={() => handleConfirmModalEditProcess()}
        okText={useTranslation({ id: "yes" })}
        cancelText={useTranslation({ id: "no" })}
        destroyOnClose={true}
      >
        <p>{useTranslation({ id: "cancel_edit" })}</p>
      </Modal>
      <button data-testid="drawer-edit-process" onClick={handleClickCancelEditProcess} style={S.FakeBtnConfirm} />
    </S.ProcessContainer>
  );
};

const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...ProcessActions, ...UserSessionActions }, dispatch);

const mapStateToProps = (state) => ({
  items: state.Process.items,
  totalItems: state.Process.totalItems,
  orderBy: state.Process.filter.orderBy,
  ascending: state.Process.filter.ascending,
  itemsByPage: state.Process.filter.itemsByPage,
  pageIndex: state.Process.filter.pageIndex,
  isAdmin: state.UserSession.isAdmin,
  searchFilter: state.Process.searchFilter,
  editProcess: state.Process.editProcess,
  documentsList: state.Process.documentsList,
  visibleDrawerEditProcess: state.Process.visibleDrawerEditProcess,
  formEditModified: state.Process.formEditModified,
  eventInboundActive: state.Process.eventInboundActive,
  eventOutboundActive: state.Process.eventOutboundActive,
  visibleDocuments: state.Process.visibleDocuments,
  isLoadingTable: state.Process.isLoadingTable,
  allowImageCapture: state.Process.allowImageCapture,
});

export default connect(mapStateToProps, mapDispatchToActions)(ProcessGrid);
